<template>
  <DashboardWidget
    title="Campaigns"
    icon="fa fa-bullhorn"
    :view-all-link="{ name: 'campaigns' }"
    :loading="loading"
    :is-empty="!hasCampaigns"
    empty-icon="fa fa-bullhorn"
    empty-title="No campaigns yet"
    empty-text="Create your first campaign to reach patients"
  >
    <div class="campaigns-summary">
      <!-- Mini Chart Section -->
      <div class="mini-chart-section">
        <div class="chart-summary">
          <div class="summary-main">
            <span class="summary-value">{{ totalSentThisWeek }}</span>
            <span v-if="weeklyTrend" class="summary-trend" :class="weeklyTrend > 0 ? 'trend-up' : 'trend-down'">
              <i :class="weeklyTrend > 0 ? 'fa fa-arrow-up' : 'fa fa-arrow-down'"></i>
              {{ Math.abs(weeklyTrend) }}%
            </span>
          </div>
          <span class="summary-label">Sent this week</span>
        </div>
        <div class="mini-sparkline">
          <svg viewBox="0 0 100 32" preserveAspectRatio="none">
            <!-- Gradient fill -->
            <defs>
              <linearGradient id="campaignGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stop-color="#8b5cf6" stop-opacity="0.3" />
                <stop offset="100%" stop-color="#8b5cf6" stop-opacity="0.02" />
              </linearGradient>
            </defs>
            <path :d="areaPath" fill="url(#campaignGradient)" />
            <polyline
              :points="sparklinePoints"
              fill="none"
              stroke="#8b5cf6"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
      </div>

      <!-- Stats Row -->
      <div class="stats-row">
        <div class="stat-item">
          <span class="stat-value">{{ activeCampaignsCount }}</span>
          <span class="stat-label">Active</span>
        </div>
        <div class="stat-item">
          <span class="stat-value">{{ scheduledCampaignsCount }}</span>
          <span class="stat-label">Scheduled</span>
        </div>
        <div class="stat-item">
          <span class="stat-value">{{ draftCampaignsCount }}</span>
          <span class="stat-label">Drafts</span>
        </div>
        <div class="stat-item highlight">
          <span class="stat-value">{{ totalSentThisWeek }}</span>
          <span class="stat-label">Sent</span>
        </div>
      </div>

      <!-- Recent Campaigns List -->
      <div v-if="recentCampaigns.length > 0" class="campaigns-list">
        <div class="list-header">Recent Campaigns</div>
        <div
          v-for="campaign in recentCampaigns"
          :key="campaign.CampaignID"
          class="list-item campaign-item"
          @click="goToCampaign(campaign)"
        >
          <div class="item-avatar campaign-avatar" :class="getStatusClass(campaign.Status)">
            <i :class="getChannelIcon(campaign.Channel)"></i>
          </div>
          <div class="item-content">
            <div class="item-header">
              <span class="item-title">{{ campaign.Name }}</span>
              <span class="item-badge" :class="getStatusClass(campaign.Status)">
                {{ campaign.Status }}
              </span>
            </div>
            <p class="item-preview">
              {{ campaign.RecipientCount || 0 }} recipients
              <span v-if="campaign.SentAt"> &bull; Sent {{ getTimeAgo(campaign.SentAt) }}</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  </DashboardWidget>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import DashboardWidget from './DashboardWidget.vue';
import moment from 'moment-timezone';
import mixin from '../../../mixin.js';

export default {
  name: 'CampaignsWidget',

  components: {
    DashboardWidget
  },

  mixins: [mixin],

  props: {
    maxItems: {
      type: Number,
      default: 3
    },
    // Chart data passed from parent
    chartData: {
      type: Array,
      default: () => [5, 12, 8, 15, 10, 18, 14]
    },
    totalSent: {
      type: [String, Number],
      default: null
    },
    trend: {
      type: Number,
      default: null
    }
  },

  data() {
    return {
      isLoadingCampaigns: true
    };
  },

  computed: {
    ...mapState('campaigns', ['campaigns']),
    ...mapGetters('campaigns', ['activeCampaigns', 'draftCampaigns', 'completedCampaigns']),

    loading() {
      return this.isLoadingCampaigns;
    },

    hasCampaigns() {
      return this.campaigns && this.campaigns.length > 0;
    },

    activeCampaignsCount() {
      return this.activeCampaigns ? this.activeCampaigns.filter(c => c.Status === 'Active').length : 0;
    },

    scheduledCampaignsCount() {
      return this.activeCampaigns ? this.activeCampaigns.filter(c => c.Status === 'Scheduled').length : 0;
    },

    draftCampaignsCount() {
      return this.draftCampaigns ? this.draftCampaigns.length : 0;
    },

    // Total sent this week - use prop or calculate from chart data
    totalSentThisWeek() {
      if (this.totalSent !== null) return this.totalSent;
      return this.chartData.reduce((a, b) => a + b, 0);
    },

    // Weekly trend percentage
    weeklyTrend() {
      return this.trend;
    },

    // Generate sparkline points for SVG polyline
    sparklinePoints() {
      const data = this.chartData;
      if (!data || data.length === 0) return '';

      const width = 100;
      const height = 32;
      const padding = 4;
      const max = Math.max(...data) || 1;
      const min = 0;

      return data.map((val, i) => {
        const x = padding + (i / (data.length - 1)) * (width - padding * 2);
        const y = padding + (height - padding * 2) - ((val - min) / (max - min)) * (height - padding * 2);
        return `${x},${y}`;
      }).join(' ');
    },

    // Generate area path for gradient fill
    areaPath() {
      const data = this.chartData;
      if (!data || data.length === 0) return '';

      const width = 100;
      const height = 32;
      const padding = 4;
      const max = Math.max(...data) || 1;
      const min = 0;

      const points = data.map((val, i) => {
        const x = padding + (i / (data.length - 1)) * (width - padding * 2);
        const y = padding + (height - padding * 2) - ((val - min) / (max - min)) * (height - padding * 2);
        return { x, y };
      });

      let path = `M ${padding},${height - padding}`;
      points.forEach((point) => {
        path += ` L ${point.x},${point.y}`;
      });
      path += ` L ${width - padding},${height - padding} Z`;

      return path;
    },

    recentCampaigns() {
      if (!this.campaigns) return [];
      // Sort by CreatedAt or SentAt descending, take top N
      return [...this.campaigns]
        .sort((a, b) => {
          const dateA = new Date(a.SentAt || a.CreatedAt || 0);
          const dateB = new Date(b.SentAt || b.CreatedAt || 0);
          return dateB - dateA;
        })
        .slice(0, this.maxItems);
    }
  },

  async mounted() {
    await this.loadCampaigns();
  },

  methods: {
    ...mapActions('campaigns', ['fetchCampaigns']),

    async loadCampaigns() {
      this.isLoadingCampaigns = true;
      try {
        await this.fetchCampaigns();
      } catch (error) {
        console.error('Error loading campaigns:', error);
      } finally {
        this.isLoadingCampaigns = false;
      }
    },

    goToCampaign(campaign) {
      this.$router.push({ name: 'CampaignDetails', params: { id: campaign.CampaignID } });
    },

    getTimeAgo(date) {
      if (!date) return '';
      return moment(date).fromNow();
    },

    getStatusClass(status) {
      switch (status) {
        case 'Active':
          return 'status-active';
        case 'Scheduled':
          return 'status-scheduled';
        case 'Completed':
          return 'status-completed';
        case 'Draft':
        default:
          return 'status-draft';
      }
    },

    getChannelIcon(channel) {
      switch (channel) {
        case 'SMS':
          return 'fa fa-mobile';
        case 'Email':
          return 'fa fa-envelope';
        case 'Both':
          return 'fa fa-paper-plane';
        default:
          return 'fa fa-bullhorn';
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.campaigns-summary {
  // Mini Chart Section
  .mini-chart-section {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    background: linear-gradient(135deg, rgba(139, 92, 246, 0.04) 0%, rgba(139, 92, 246, 0.01) 100%);
    border-bottom: 1px solid #e8ecf1;
  }

  .chart-summary {
    flex-shrink: 0;
  }

  .summary-main {
    display: flex;
    align-items: baseline;
    gap: 10px;
    margin-bottom: 2px;
  }

  .summary-value {
    font-size: 1.75rem;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.1;
  }

  .summary-trend {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 0.75rem;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 12px;

    i {
      font-size: 0.625rem;
    }

    &.trend-up {
      background-color: rgba(34, 197, 94, 0.1);
      color: #16a34a;
    }

    &.trend-down {
      background-color: rgba(239, 68, 68, 0.1);
      color: #dc2626;
    }
  }

  .summary-label {
    font-size: 0.8125rem;
    color: #64748b;
  }

  .mini-sparkline {
    width: 120px;
    height: 32px;
    flex-shrink: 0;

    svg {
      width: 100%;
      height: 100%;
    }
  }

  // Stats Row
  .stats-row {
    display: flex;
    justify-content: space-evenly;
    padding: var(--widget-stat-padding, 12px 20px);
    border-bottom: 1px solid #e8ecf1;
    margin-bottom: 0;
    min-height: 50px;
  }

  .stat-item {
    display: block !important;
    text-align: center;
    flex: 1;
    min-width: 0;
    padding: 0 8px !important;
    border-left: none !important;

    &.highlight {
      .stat-value {
        color: #8b5cf6;
      }
    }
  }

  .stat-value {
    display: block;
    font-size: var(--widget-stat-size, 1.25rem);
    font-weight: 700;
    color: #334155;
    line-height: 1.2;
  }

  .stat-label {
    font-size: 0.625rem;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-top: 2px;
  }

  // Campaigns List
  .campaigns-list {
    .list-header {
      font-size: 0.75rem;
      font-weight: 600;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      padding: var(--widget-list-padding, 12px 20px 8px 20px);
    }
  }

  .campaign-item {
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: #f8fafc;
    }
  }

  .campaign-avatar {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;

    &.status-active {
      background-color: rgba(34, 197, 94, 0.1);
      color: #22c55e;
    }

    &.status-scheduled {
      background-color: rgba(59, 130, 246, 0.1);
      color: #3b82f6;
    }

    &.status-completed {
      background-color: rgba(100, 116, 139, 0.1);
      color: #64748b;
    }

    &.status-draft {
      background-color: rgba(251, 191, 36, 0.1);
      color: #f59e0b;
    }

    i {
      font-size: 1rem;
    }
  }

  .item-badge {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 0.6875rem;
    font-weight: 500;

    &.status-active {
      background-color: rgba(34, 197, 94, 0.1);
      color: #22c55e;
    }

    &.status-scheduled {
      background-color: rgba(59, 130, 246, 0.1);
      color: #3b82f6;
    }

    &.status-completed {
      background-color: rgba(100, 116, 139, 0.1);
      color: #64748b;
    }

    &.status-draft {
      background-color: rgba(251, 191, 36, 0.1);
      color: #f59e0b;
    }
  }
}
</style>
